/*
 * Help Center - sticky tab nav with scroll-spy and the transparent
 * "stuck" skin. Pure UI: no API calls, no state.
 *
 * Same pattern as the Superstar page's bindStickyTabs(), kept in its
 * own file so the Help Center isn't tied to the loyalty bundle.
 */

(() => {
    'use strict'

    const root = document.querySelector('.hc')
    if (!root) return

    const tabs = root.querySelectorAll('[data-tabs-bar] .hc-tab')
    if (!tabs.length) return

    // Smooth-scroll on tab click, accounting for the theme header height
    // + the sticky tab bar's own height + a small gap.
    tabs.forEach((tab) => {
        tab.addEventListener('click', (e) => {
            e.preventDefault()
            const id = tab.getAttribute('href').slice(1)
            const target = document.getElementById(id)
            if (!target) return
            const headSpace = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--headSpace')) || 0
            const offset = headSpace + 56 + 12
            const top = target.getBoundingClientRect().top + window.scrollY - offset
            setActive(id)
            window.scrollTo({ top, behavior: 'smooth' })
        })
    })

    // Scroll-spy: whichever section is closest to the top of the visible
    // area wins the active state. Section IDs come from the tab list, so
    // adding/removing tabs in the Liquid section is enough.
    const sectionIds = Array.from(tabs).map((t) => t.dataset.tab)
    const sections = sectionIds
        .map((id) => document.getElementById(id))
        .filter(Boolean)

    function setActive(id) {
        tabs.forEach((t) => t.classList.toggle('is-active', t.dataset.tab === id))
        positionIndicator()
    }

    const io = new IntersectionObserver(
        (entries) => {
            const visible = entries
                .filter((e) => e.isIntersecting)
                .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
            if (visible[0]) setActive(visible[0].target.id)
        },
        { rootMargin: '-80px 0px -60% 0px', threshold: 0 },
    )
    sections.forEach((s) => io.observe(s))

    // Stuck detection - flip the bar to translucent+blur once it pins
    // under the theme header. The sentinel above the bar acts as the
    // trigger: when it scrolls past the header, the bar is stuck.
    const bar = root.querySelector('[data-tabs-bar]')
    const sentinel = root.querySelector('[data-tabs-sentinel]')
    if (bar && sentinel) {
        const headSpace = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--headSpace')) || 0
        const stickIo = new IntersectionObserver(
            ([entry]) => bar.classList.toggle('is-stuck', !entry.isIntersecting),
            { rootMargin: `-${headSpace}px 0px 0px 0px`, threshold: 0 },
        )
        stickIo.observe(sentinel)
    }

    // Position the active-tab underline.
    function positionIndicator() {
        const indicator = root.querySelector('[data-tab-indicator]')
        const active = root.querySelector('[data-tabs-bar] .hc-tab.is-active')
        if (!indicator || !active) return
        const barRect = active.parentElement.getBoundingClientRect()
        const r = active.getBoundingClientRect()
        indicator.style.transform = `translateX(${r.left - barRect.left}px)`
        indicator.style.width = `${r.width}px`
    }
    window.addEventListener('resize', positionIndicator)
    requestAnimationFrame(positionIndicator)

    // Reveal-on-scroll for top-level sections (matches the Superstar page
    // pattern - the `.ss-reveal` baseline + IntersectionObserver flip).
    const revealIo = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-visible')
                    revealIo.unobserve(entry.target)
                }
            })
        },
        { threshold: 0.12, rootMargin: '0px 0px -40px 0px' },
    )
    root.querySelectorAll('.ss-reveal').forEach((el) => revealIo.observe(el))
})()
